import {
  QueryKey,
  UseInfiniteQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import keyBy from 'lodash/keyBy';
import { ReactNode, useMemo, useState } from 'react';

import { SearchSelect } from '@/components/ui/daisy';
import {
  SearchSelectProps,
  SearchSelectValue,
} from '@/components/ui/daisy/templates/search-select/types';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import JsonApiResponse from '@/models/JsonApiResponse';
import { Model as BaseModel, Nullable } from '@/models/Model';
import { BaseFilters, GetServiceReturn, ServiceReturn } from '@/models/Service';

const IDLE_QUERY_KEY: QueryKey = ['infinite-search-select', 'idle'];
const IDLE_QUERY_FN = () => Promise.resolve(null);

type PassthroughProps = Omit<
  SearchSelectProps,
  | 'value'
  | 'onChange'
  | 'onSearch'
  | 'onReachEnd'
  | 'loading'
  | 'hasNextPage'
  | 'isFetchingNextPage'
  | 'selectedLabel'
  | 'children'
>;

export type InfiniteSearchSelectProps<
  Filter extends BaseFilters,
  Model extends BaseModel,
> = PassthroughProps & {
  listService: (filters?: Filter) => ServiceReturn<Model>;
  getService: (id: SearchSelectValue) => GetServiceReturn<Model>;
  filters?: Filter;
  renderOption?: (item: Model) => ReactNode;
  renderSelectedAddon?: (item: Model) => ReactNode;
  getOptionLabel: (item: Model) => string;
  getOptionValue?: (item: Model) => SearchSelectValue;
  value?: SearchSelectValue | null;
  onChange?: (value: Model | null) => void;
  clearable?: boolean;
  isLoading?: boolean;
  children?: ReactNode;
  queryOptions?: Omit<
    UseInfiniteQueryOptions<JsonApiResponse<Model>>,
    'queryKey'
  >;
};

export default function InfiniteSearchSelect<
  Filter extends BaseFilters,
  Model extends BaseModel,
>({
  listService,
  getService,
  filters,
  getOptionLabel,
  renderOption = getOptionLabel,
  renderSelectedAddon,
  getOptionValue = item => item.id,
  value,
  onChange,
  clearable = true,
  isLoading,
  children,
  queryOptions,
  ...searchSelectProps
}: InfiniteSearchSelectProps<Filter, Model>) {
  const [search, setSearch] = useState<string>();

  const {
    results,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...queryOptions,
    keepPreviousData: true,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    ...listService({ ...filters, search } as Filter)._ctx.infinity,
  });

  const resultsById = useMemo(
    () => keyBy(results, getOptionValue),
    [results, getOptionValue],
  );

  const selectedFromResults = value != null ? resultsById[value] : undefined;

  const selectedQuery = value != null ? getService(value) : undefined;

  const { data: fetchedSelected, isInitialLoading: isSelectedLoading } =
    useQuery<Nullable<Model>>({
      queryKey: selectedQuery?.queryKey ?? IDLE_QUERY_KEY,
      queryFn: selectedQuery?.queryFn ?? IDLE_QUERY_FN,
      enabled: value != null && !selectedFromResults && !isInitialLoading,
      staleTime: REQUEST_STALE_TIME_IN_MS,
    });

  const selectedItem = selectedFromResults ?? fetchedSelected ?? undefined;

  const handleChange = (nextValue: SearchSelectValue) =>
    onChange?.(resultsById[nextValue] ?? null);

  const currentValue = value ?? undefined;

  const selectedLabel = selectedItem ? getOptionLabel(selectedItem) : undefined;

  return (
    <SearchSelect
      {...searchSelectProps}
      value={currentValue}
      onChange={handleChange}
      onClear={clearable ? () => onChange?.(null) : undefined}
      onSearch={nextSearch => setSearch(nextSearch || undefined)}
      onReachEnd={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      loading={isLoading || isInitialLoading || isSelectedLoading}
      selectedLabel={selectedLabel}
      selectedAddon={selectedItem && renderSelectedAddon?.(selectedItem)}
    >
      {children}
      {results.map(item => {
        const optionValue = getOptionValue(item);
        return (
          <SearchSelect.Option key={optionValue} value={optionValue}>
            {renderOption(item)}
          </SearchSelect.Option>
        );
      })}
    </SearchSelect>
  );
}
